/**
 * The words for a code the platform publishes — `GET /vocabularies/{vocabulary}` (bata-platform
 * ADR 2125; owner ruling 2026-10-10: the words are the platform's, never the client's).
 *
 * The platform published codes (`OPEN_SANDAL`, `ALL_YEAR`, `ADULT_SHOE`) and screens printed them.
 * This is the ONE place a screen turns a code into its label:
 *
 *   `const { labelOf } = useVocabularyLabels('department_family')`   then   `labelOf(code)`
 *
 * **What `labelOf` answers.** The platform's label for the code, in the planner's language or —
 * where none was authored in it — in the first locale of G6's chain that has one (shown as it
 * is: a label in another language beats a code, and the audit asked for exactly that). **The code
 * itself otherwise**, spelled exactly as before: while the read is loading, when the platform
 * does not publish the read yet (404), when it has no source installed (503
 * `platform.foundations.vocabularies_unavailable`), when it failed, and when the code has
 * `label: null` (`label_not_published`). Never «—», never an invented word, never a blank: a
 * screen that adopts this can only get better, which is what makes it safe to adopt everywhere.
 *
 * **One request per vocabulary, not one per cell.** With no `codes` the read asks for every code
 * that has a label (`?code` omitted): one stable cache entry per vocabulary and language, shared
 * by every screen of the session, which is the right shape for a small vocabulary such as the
 * size scales or the seasons — and it cannot flash when a screen's rows change. With `codes` the
 * request names exactly those, distinct and sorted so the key is stable, in batches of the
 * platform's bound (200); for a vocabulary that could be large. An empty `codes` asks nothing.
 * Labels change only by a governed change set, so the answer is cached for the session
 * (`SESSION_LONG_READ`) and the read is a background one: it never holds a placeholder busy.
 *
 * **In exports.** A file keeps the CODE in its code column (ADR 9058) and writes the words only
 * where the screen writes them in a NAME column that the platform leaves empty
 * (`readVocabularyLabels`, bata-ui labels fix): one read of the screen's own cache entry, never a
 * walk of its own, and never a failure of the file — the code stands where no label is published.
 *
 * Outside a `QueryClientProvider` (a unit test of a primitive) nothing is asked and every code
 * stands for itself.
 */

import { QueryClient, QueryClientContext, useQueries } from '@tanstack/react-query';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { BACKGROUND_READ } from '../primitives/read-activity';
import {
  foundationsReadVocabulary as VOCABULARY_READ,
  type FoundationsReadVocabularyParams as VocabularyParams,
  type VocabularyTermModel as VocabularyTermShape,
  type VocabularyTermsResponse as VocabularyTermsShape,
} from './generated';
import { callOperation } from './http';
import { ApiProblemError } from './problem';
import { registeredQueryClient, SESSION_LONG_READ } from './session-cache';

/**
 * The vocabularies the audit found shown as raw codes. The read accepts any name (G5): the name
 * is DATA, the snake-case name of the code's kind.
 */
export const VOCABULARY_DEPARTMENT_FAMILY = 'department_family';
export const VOCABULARY_SEASON = 'season';
export const VOCABULARY_SIZE_SCALE = 'size_scale';
/** A store's format (`HIGH_STREET`) and a sales channel (`store`, `OUTLET`): audit 2, E1. Labels are published as registry rows; until they are, the code stands. */
export const VOCABULARY_STORE_FORMAT = 'store_format';
export const VOCABULARY_CHANNEL = 'channel';
/** An exit plan's channel (`RETAIL_OWN_STORE`, `planning.exit.channel`): the platform publishes its labels. */
export const VOCABULARY_EXIT_CHANNEL = 'exit_channel';

/** The most `code` values one request may name (the platform's request-size bound, G16). */
export const VOCABULARY_MAXIMUM_CODES = 200;

/** A code as the planner reads it: the platform's label, else the code. */
export type VocabularyLabeller = (code: string) => string;

export interface VocabularyLabels {
  /** The label of `code`, or `code` itself where there is none (loading, unwired, not published). */
  readonly labelOf: VocabularyLabeller;
  /** The platform's label of `code`, or `null` — for a caller that must tell a word from a code. */
  readonly labelOrNull: (code: string) => string | null;
}

/** A label that says something: a published `label` that is not blank. */
function wordsOf(term: VocabularyTermShape): string | null {
  return typeof term.label === 'string' && term.label.trim() !== '' ? term.label : null;
}

/**
 * The labeller over `terms` — pure. A code with no term, or a term whose `label` is `null`
 * (`label_not_published`) or blank, stands for itself.
 */
export function labelsFrom(terms: readonly VocabularyTermShape[] | undefined): VocabularyLabels {
  const byCode = new Map<string, string>();
  for (const term of terms ?? []) {
    const words = wordsOf(term);
    if (words !== null) byCode.set(term.code, words);
  }
  return {
    labelOf: (code) => byCode.get(code) ?? code,
    labelOrNull: (code) => byCode.get(code) ?? null,
  };
}

/** The labeller of no terms: every code stands for itself. */
export const NO_LABELS: VocabularyLabels = labelsFrom(undefined);

/** `codes` as the requests name them: distinct, blank-free, sorted (a stable key), in batches of the bound. */
export function codeBatches(codes: readonly string[]): readonly (readonly string[])[] {
  const distinct = [...new Set(codes.filter((code) => code.trim() !== ''))].sort();
  const batches: string[][] = [];
  for (let at = 0; at < distinct.length; at += VOCABULARY_MAXIMUM_CODES) {
    batches.push(distinct.slice(at, at + VOCABULARY_MAXIMUM_CODES));
  }
  return batches;
}

/**
 * Whether the platform has said it does not publish labels (yet): the route is absent (404) or
 * no source is installed (503). That answer is not a fault to retry on every screen — it is
 * «no labels», kept for the session like a label would be.
 */
export function isUnwired(error: unknown): boolean {
  return error instanceof ApiProblemError && (error.status === 404 || error.status === 503);
}

const NO_TERMS: VocabularyTermsShape = { vocabulary: '', locale: '', as_of: '', terms: [] };

/** The one client the hook reads from when there is no provider; it never fetches (`enabled: false`). */
const INERT_CLIENT = new QueryClient();

/** Every term of every answer — an array, so the result keeps its identity while the answers do. */
function combineTerms(results: readonly { readonly data: VocabularyTermsShape | undefined }[]): readonly VocabularyTermShape[] {
  const terms: VocabularyTermShape[] = [];
  for (const result of results) {
    const answered = result.data?.terms;
    if (!Array.isArray(answered)) continue; // a stand-in or an older platform answering another shape: no labels
    for (const term of answered as readonly VocabularyTermShape[]) {
      if (typeof term?.code === 'string') terms.push(term);
    }
  }
  return terms;
}

/**
 * The ONE query of a vocabulary's terms — key, read and cache policy — shared by the hook (the
 * screens) and {@link readVocabularyLabels} (the files), so both read the same cache entry.
 * `batch` omitted asks for every labelled code.
 */
function vocabularyQuery(vocabulary: string, batch: readonly string[] | undefined, language: string) {
  const params: VocabularyParams = batch === undefined
    ? { path: { vocabulary } }
    : { path: { vocabulary }, query: { code: batch } };
  return {
    queryKey: [VOCABULARY_READ.operationId, params, language] as const,
    queryFn: async ({ signal }: { signal: AbortSignal }): Promise<VocabularyTermsShape> => {
      try {
        return await callOperation(VOCABULARY_READ, params, { language, signal });
      } catch (error) {
        if (isUnwired(error)) return NO_TERMS;
        throw error;
      }
    },
    retry: false,
    ...SESSION_LONG_READ,
  };
}

/**
 * The labels of `vocabulary`'s codes — see the file's comment. `codes` omitted asks for every
 * labelled code of the vocabulary; named, for those only.
 */
export function useVocabularyLabels(vocabulary: string, codes?: readonly string[]): VocabularyLabels {
  const { i18n } = useTranslation();
  const language = i18n.language;
  const client = useContext(QueryClientContext);

  // The codes by content: a re-render with the same codes, in another order or twice, asks nothing new.
  const key = codes === undefined ? null : codeBatches(codes).map((batch) => batch.join('\u0000')).join('\u0001');
  const batches = useMemo(
    () => (key === null ? [undefined] : codeBatches((key === '' ? [] : key.split('\u0001').flatMap((batch) => batch.split('\u0000'))))),
    [key],
  );

  const terms = useQueries(
    {
      queries: batches.map((batch) => ({
        ...vocabularyQuery(vocabulary, batch, language),
        enabled: client !== undefined,
        ...BACKGROUND_READ,
      })),
      combine: combineTerms,
    },
    client ?? INERT_CLIENT,
  );

  return useMemo(() => labelsFrom(terms), [terms]);
}

/** The label of a department family code (`OPEN_SANDAL`) — the family, as the planner reads it. */
export function useFamilyLabel(): VocabularyLabeller {
  return useVocabularyLabels(VOCABULARY_DEPARTMENT_FAMILY).labelOf;
}

/** The label of a season code (`ALL_YEAR`). */
export function useSeasonLabel(): VocabularyLabeller {
  return useVocabularyLabels(VOCABULARY_SEASON).labelOf;
}

/** The label of a store format code (`HIGH_STREET`). */
export function useStoreFormatLabel(): VocabularyLabeller {
  return useVocabularyLabels(VOCABULARY_STORE_FORMAT).labelOf;
}

/** The label of an exit channel code (`RETAIL_OWN_STORE`). */
export function useExitChannelLabel(): VocabularyLabeller {
  return useVocabularyLabels(VOCABULARY_EXIT_CHANNEL).labelOf;
}

/** The label of a channel code (`store`, `OUTLET`). */
export function useChannelLabel(): VocabularyLabeller {
  return useVocabularyLabels(VOCABULARY_CHANNEL).labelOf;
}

/** The label of a size scale code (`ADULT_SHOE`) — the scale a size is read in. */
export function useSizeScaleLabel(): VocabularyLabeller {
  return useVocabularyLabels(VOCABULARY_SIZE_SCALE).labelOf;
}

/**
 * The labels of every labelled code of `vocabulary`, read outside React — for a file built on a
 * click (an export) that must say a group's name in the words its screen does.
 *
 * It asks for the SAME cache entry `useVocabularyLabels(vocabulary)` reads (`ensureQueryData`): a
 * screen that already drew the words costs the file no request at all, and one that did not costs
 * one request that the screen then finds warm. With no client registered (a unit test, a script)
 * it is the plain read. **It never rejects**: a failure is `NO_LABELS`, so the code stands for
 * itself exactly as it does on the screen, and the file is still produced.
 */
export async function readVocabularyLabels(vocabulary: string, language: string): Promise<VocabularyLabels> {
  const query = vocabularyQuery(vocabulary, undefined, language);
  try {
    const client = registeredQueryClient();
    const answered = client === null
      ? await query.queryFn({ signal: new AbortController().signal })
      : await client.ensureQueryData(query);
    return labelsFrom(combineTerms([{ data: answered }]));
  } catch {
    return NO_LABELS;
  }
}
